import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { companyService, Company } from '../services/companyService';
import { invoiceService, HoursPreview } from '../services/invoiceService';
import { dealService, Deal } from '../services/dealService';
import {
  PROJECTS_MONTHLY_FEE,
  companyBillsProjectsModule,
  isProjectsMonthlyItem,
  projectsMonthlyItem
} from '../utils/projectsBilling';
import { dealAmount, dealToInvoiceItem, isDealItem } from '../utils/dealsBilling';
import './CreateInvoice.css';

interface ItemRow { code?: string; description: string; quantity: number | null; unitPrice: number | null; }

const CreateInvoice: React.FC = () => {
  const [mode, setMode] = useState<'manual' | 'hours'>('manual');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [clientCompanyId, setClientCompanyId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientCity, setClientCity] = useState('');
  const [clientState, setClientState] = useState('');
  const [clientCountry, setClientCountry] = useState('');
  const [clientZipCode, setClientZipCode] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientTaxId, setClientTaxId] = useState('');
  const [items, setItems] = useState<ItemRow[]>([{ description: '', quantity: 1, unitPrice: 0 }]);
  const [notes, setNotes] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [swiftCode, setSwiftCode] = useState('');
  const [iban, setIban] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState<Date | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [hoursPreview, setHoursPreview] = useState<HoursPreview | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const fromHours = mode === 'hours';
  const clientCompanies = companies.filter((c) => !c.isOwner && c.type !== 'SERVICE_PROVIDER');
  const companyOptions = clientCompanies.map((c) => ({ label: `${c.name} (${c.email})`, value: c._id }));
  const selectedCompany = companies.find((company) => company._id === clientCompanyId);
  const canAddProjectsFee = companyBillsProjectsModule(selectedCompany);
  const projectsFeeOnInvoice = items.some((item) => isProjectsMonthlyItem(item));
  const activeDeals = deals.filter((deal) => deal.status === 'ACTIVE');
  const isAddonItem = (item: { code?: string; description?: string }) =>
    isProjectsMonthlyItem(item) || isDealItem(item);
  const displayTotal = fromHours ? (hoursPreview?.amount || 0) : items.reduce((sum, it) => sum + ((it.quantity || 0) * (it.unitPrice || 0)), 0);

  useEffect(() => {
    (async () => {
      try {
        const [list, issuer] = await Promise.all([
          companyService.getCompanies(),
          invoiceService.getIssuerProfile()
        ]);
        setCompanies(list);
        setSwiftCode(issuer.issuerSwiftCode || '');
        setIban(issuer.issuerIban || '');
        setCardNumber(issuer.issuerCardNumber || '');
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (!clientCompanyId) return;
    const company = companies.find((c) => c._id === clientCompanyId);
    if (!company) return;
    setClientName(company.billing?.contactName || company.name);
    setClientEmail(company.email);
    setClientCompany(company.name);
    setClientAddress(company.address?.street || '');
    setClientCity(company.address?.city || '');
    setClientState(company.address?.state || '');
    setClientCountry(company.address?.country || '');
    setClientZipCode(company.address?.zipCode || '');
    setClientPhone(company.phone || '');
    setClientTaxId(company.billing?.taxId || '');
    setHoursPreview(null);
    setItems((prev) => {
      const kept = prev.filter((item) => !isProjectsMonthlyItem(item) && !isDealItem(item));
      return kept.length ? kept : [{ description: '', quantity: 1, unitPrice: 0 }];
    });
  }, [clientCompanyId, companies]);

  useEffect(() => {
    if (!clientCompanyId) {
      setDeals([]);
      return;
    }
    dealService.list(clientCompanyId, 'ACTIVE')
      .then(setDeals)
      .catch(() => setDeals([]));
  }, [clientCompanyId]);

  const addItem = () => setItems((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setItem = (idx: number, key: keyof ItemRow, value: any) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));

  const addDealItem = (deal: Deal) => {
    const row = dealToInvoiceItem(deal);
    setItems((prev) => {
      if (prev.some((item) => item.code === row.code)) {
        return prev;
      }
      const placeholder = prev.length === 1 && !prev[0].description && !prev[0].unitPrice;
      return placeholder ? [row] : [...prev, row];
    });
  };

  const addProjectsItem = () => {
    const fee = projectsMonthlyItem();
    setItems((prev) => {
      if (prev.some((item) => isProjectsMonthlyItem(item))) {
        return prev;
      }
      const placeholder = prev.length === 1 && !prev[0].description && !prev[0].unitPrice;
      return placeholder ? [fee] : [...prev, fee];
    });
  };

  const loadHoursPreview = async () => {
    if (!clientCompanyId) {
      setError('Select a company first');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const preview = await invoiceService.previewHours(
        clientCompanyId,
        fromDate ? fromDate.toISOString() : undefined,
        toDate ? toDate.toISOString() : undefined
      );
      setHoursPreview(preview);
      if (preview.missingRate) {
        setError(preview.missingRateReason || 'Set an hourly rate on this company before generating from hours');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to preview hours');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (fromHours) {
        if (!clientCompanyId) {
          setError('Select a company');
          return;
        }
        await invoiceService.createFromHours({
          companyId: clientCompanyId,
          from: fromDate ? fromDate.toISOString() : undefined,
          to: toDate ? toDate.toISOString() : undefined,
          dueDate: dueDate || undefined,
          notes,
          description
        });
        setSuccess('Invoice generated from unbilled hours.');
        setHoursPreview(null);
      } else {
        let reminderHour: number | undefined;
        let reminderMinute: number | undefined;
        if (reminderEnabled && reminderTime instanceof Date) {
          reminderHour = reminderTime.getHours();
          reminderMinute = reminderTime.getMinutes();
        }

        await invoiceService.createInvoice({
          clientCompany: clientCompanyId || undefined,
          clientName,
          clientEmail,
          clientCompanyName: clientCompany,
          clientAddress,
          clientCity,
          clientState,
          clientCountry,
          clientZipCode,
          clientPhone,
          clientTaxId,
          items: items.map((i) => ({
            code: i.code,
            description: i.description,
            quantity: i.quantity || 0,
            unitPrice: i.unitPrice || 0
          })),
          amount: displayTotal,
          notes,
          description,
          dueDate: dueDate as Date,
          reminderEnabled,
          ...(reminderHour !== undefined && reminderMinute !== undefined ? { reminderHour, reminderMinute } : {})
        });
        setSuccess('Invoice created successfully.');
      }

      setClientCompanyId(null);
      setClientName('');
      setClientEmail('');
      setClientCompany('');
      setClientAddress('');
      setClientCity('');
      setClientState('');
      setClientCountry('');
      setClientZipCode('');
      setClientPhone('');
      setClientTaxId('');
      setItems([{ description: '', quantity: 1, unitPrice: 0 }]);
      setNotes('');
      setDescription('');
      setDueDate(null);
      setReminderEnabled(false);
      setReminderTime(null);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="invoice-create">
      <div className="invoice-create-header">
        <div>
          <h1>Create invoice</h1>
          <p>Fill the bill-to details, then add line items or generate from tracked hours.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="invoice-create-form">
        <section className="invoice-panel mode-panel">
          <div className={`mode-toggle ${fromHours ? 'hours' : 'manual'}`}>
            <div className={`mode-side ${!fromHours ? 'active' : ''}`}>
              <i className="pi pi-pencil"></i>
              <div>
                <strong>Manual / retroactive</strong>
                <span>Enter everything yourself</span>
              </div>
            </div>
            <InputSwitch
              checked={fromHours}
              onChange={(e) => {
                setMode(e.value ? 'hours' : 'manual');
                setError('');
                setSuccess('');
              }}
            />
            <div className={`mode-side ${fromHours ? 'active' : ''}`}>
              <i className="pi pi-clock"></i>
              <div>
                <strong>From hours</strong>
                <span>Unbilled Helpdesk time</span>
              </div>
            </div>
          </div>
        </section>

        <section className="invoice-panel">
          <div className="panel-title">
            <i className="pi pi-building"></i>
            <h2>Company</h2>
          </div>
          <div className="field-grid">
            <div className="field full">
              <label>Company</label>
              <Dropdown
                value={clientCompanyId}
                options={companyOptions}
                onChange={(e) => setClientCompanyId(e.value)}
                placeholder="Select company"
                filter
                showClear
                className="w-full"
              />
            </div>
          </div>
        </section>

        {fromHours && (
          <section className="invoice-panel hours-panel">
            <div className="panel-title">
              <i className="pi pi-clock"></i>
              <h2>Unbilled hours</h2>
            </div>
            <p className="panel-copy">Leave dates empty to include all unbilled time for this company.</p>
            <div className="hours-controls">
              <div className="field">
                <label>From</label>
                <Calendar
                  value={fromDate}
                  onChange={(e) => setFromDate(e.value as Date)}
                  placeholder="Any start"
                  className="date-field"
                />
              </div>
              <div className="field">
                <label>To</label>
                <Calendar
                  value={toDate}
                  onChange={(e) => setToDate(e.value as Date)}
                  placeholder="Any end"
                  className="date-field"
                />
              </div>
              <Button
                type="button"
                label="Preview"
                icon="pi pi-search"
                className="preview-btn"
                onClick={loadHoursPreview}
                loading={loading}
              />
            </div>
            {hoursPreview ? (
              <div className="hours-preview">
                <div className="hours-preview-summary">
                  <div>
                    <small>Hours</small>
                    <strong>{hoursPreview.totalHours}</strong>
                  </div>
                  <div>
                    <small>Entries</small>
                    <strong>{hoursPreview.entryCount}</strong>
                  </div>
                  <div>
                    <small>Amount</small>
                    <strong>{hoursPreview.amount.toLocaleString('en-US', { style: 'currency', currency: hoursPreview.currency })}</strong>
                  </div>
                </div>
                {hoursPreview.items.length > 0 ? (
                  hoursPreview.items.map((item, index) => (
                    <div key={`${item.code || item.description}-${index}`} className="hours-preview-row">
                      <span>{item.description}</span>
                      <span>
                        {isAddonItem(item)
                          ? item.amount.toLocaleString('en-US', { style: 'currency', currency: hoursPreview.currency })
                          : `${item.quantity}h × ${item.unitPrice}`}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="hours-empty">
                    {hoursPreview.support
                      ? 'No extra hours to bill. Time in the support package is covered by the deal.'
                      : 'No unbilled hours in this period.'}
                  </p>
                )}
                {hoursPreview.support && (
                  <p className="hours-fee-note">
                    Support package {hoursPreview.support.usedHours}/{hoursPreview.support.includedHours}h used
                    {hoursPreview.support.periodLabel ? ` (${hoursPreview.support.periodLabel})` : ''}.
                    {hoursPreview.support.overageHours > 0
                      ? ` ${hoursPreview.support.overageHours}h overage at ${hoursPreview.support.overageRate.toLocaleString('en-US', { style: 'currency', currency: hoursPreview.currency })}/h.`
                      : ` ${hoursPreview.support.remainingHours}h remaining in the package.`}
                  </p>
                )}
                {hoursPreview.projectsFee?.included && (
                  <p className="hours-fee-note">Projects monthly fee ($29) was added automatically.</p>
                )}
                {hoursPreview.projectsFee?.eligible && hoursPreview.projectsFee.alreadyBilled && (
                  <p className="hours-fee-note">Projects fee was already invoiced this month.</p>
                )}
                {!!hoursPreview.deals?.included && (
                  <p className="hours-fee-note">
                    {hoursPreview.deals.included === 1
                      ? '1 deal was added automatically.'
                      : `${hoursPreview.deals.included} deals were added automatically.`}
                  </p>
                )}
                {!!hoursPreview.deals?.skipped && !hoursPreview.deals.included && (
                  <p className="hours-fee-note">Active deals were already invoiced this month.</p>
                )}
              </div>
            ) : (
              <p className="hours-empty">Select a company and preview hours before generating.</p>
            )}
          </section>
        )}

        {!fromHours && (
          <>
            <section className="invoice-panel">
              <div className="panel-title">
                <i className="pi pi-user"></i>
                <h2>Bill to</h2>
              </div>
              <div className="field-grid two">
                <div className="field">
                  <label>Client name</label>
                  <InputText value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                </div>
                <div className="field">
                  <label>Client email</label>
                  <InputText type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required />
                </div>
                <div className="field">
                  <label>Client company</label>
                  <InputText value={clientCompany} onChange={(e) => setClientCompany(e.target.value)} />
                </div>
                <div className="field">
                  <label>Tax ID</label>
                  <InputText value={clientTaxId} onChange={(e) => setClientTaxId(e.target.value)} />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <InputText value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
                </div>
              </div>
            </section>

            <section className="invoice-panel">
              <div className="panel-title">
                <i className="pi pi-map-marker"></i>
                <h2>Address</h2>
              </div>
              <div className="field-grid two">
                <div className="field full">
                  <label>Street</label>
                  <InputText value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} />
                </div>
                <div className="field">
                  <label>City</label>
                  <InputText value={clientCity} onChange={(e) => setClientCity(e.target.value)} />
                </div>
                <div className="field">
                  <label>State</label>
                  <InputText value={clientState} onChange={(e) => setClientState(e.target.value)} />
                </div>
                <div className="field">
                  <label>Zip code</label>
                  <InputText value={clientZipCode} onChange={(e) => setClientZipCode(e.target.value)} />
                </div>
                <div className="field">
                  <label>Country</label>
                  <InputText value={clientCountry} onChange={(e) => setClientCountry(e.target.value)} />
                </div>
              </div>
            </section>

            <section className="invoice-panel">
              <div className="panel-title">
                <i className="pi pi-list"></i>
                <h2>Line items</h2>
              </div>
              <div className="items-head">
                <span>Description</span>
                <span>Qty / hrs</span>
                <span>Unit price</span>
                <span />
              </div>
              <div className="items-list">
                {items.map((it, idx) => (
                  <div className="item-row" key={idx}>
                    <InputText placeholder="Service description" value={it.description} onChange={(e) => setItem(idx, 'description', e.target.value)} />
                    <InputNumber placeholder="Qty" value={it.quantity} onValueChange={(e) => setItem(idx, 'quantity', e.value ?? 0)} min={0} />
                    <InputNumber placeholder="Unit price" value={it.unitPrice} onValueChange={(e) => setItem(idx, 'unitPrice', e.value ?? 0)} mode="currency" currency="USD" locale="en-US" min={0} />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-danger p-button-sm p-button-rounded compact-action-btn"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                    />
                  </div>
                ))}
              </div>
              <div className="items-actions">
                <Button
                  type="button"
                  icon="pi pi-plus"
                  label="Add item"
                  onClick={addItem}
                  className="p-button-secondary add-item-btn"
                />
                {canAddProjectsFee && !projectsFeeOnInvoice && (
                  <Button
                    type="button"
                    icon="pi pi-th-large"
                    label={`Add Projects ($${PROJECTS_MONTHLY_FEE})`}
                    onClick={addProjectsItem}
                    className="new-ticket-btn add-item-btn"
                  />
                )}
                {activeDeals.map((deal) => {
                  const row = dealToInvoiceItem(deal);
                  if (items.some((item) => item.code === row.code)) {
                    return null;
                  }
                  return (
                    <Button
                      key={deal._id}
                      type="button"
                      icon="pi pi-briefcase"
                      label={`Add ${deal.name} (${dealAmount(deal).toLocaleString('en-US', { style: 'currency', currency: 'USD' })})`}
                      onClick={() => addDealItem(deal)}
                      className="new-ticket-btn add-item-btn"
                    />
                  );
                })}
              </div>
            </section>
          </>
        )}

        <section className="invoice-panel">
          <div className="panel-title">
            <i className="pi pi-file"></i>
            <h2>Invoice details</h2>
          </div>
          <div className="field-grid two">
            <div className="field">
              <label>Due date</label>
              <Calendar value={dueDate} onChange={(e) => setDueDate(e.value as Date)} placeholder="Select date" className="date-field" required={!fromHours} />
            </div>
            <div className="field full">
              <label>Description (optional)</label>
              <InputTextarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} autoResize />
            </div>
            <div className="field full">
              <label>Notes (optional)</label>
              <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} autoResize />
            </div>
          </div>
        </section>

        <section className="invoice-panel">
          <div className="panel-title">
            <i className="pi pi-credit-card"></i>
            <h2>Payment details</h2>
            <span className="panel-hint">From Uptimio issuer profile</span>
          </div>
          <div className="field-grid three">
            <div className="field">
              <label>SWIFT</label>
              <InputText value={swiftCode} disabled />
            </div>
            <div className="field">
              <label>IBAN</label>
              <InputText value={iban} disabled />
            </div>
            <div className="field">
              <label>Card number</label>
              <InputText value={cardNumber} disabled />
            </div>
          </div>
          {!fromHours && (
            <div className="reminder-row">
              <div>
                <label>Daily reminders</label>
                <p>Send a payment reminder every day at the chosen time (your timezone)</p>
              </div>
              <div className="reminder-controls">
                <InputSwitch checked={reminderEnabled} onChange={(e) => setReminderEnabled(!!e.value)} />
                <Calendar value={reminderTime} onChange={(e) => setReminderTime(e.value as Date)} timeOnly hourFormat="24" placeholder="Time" disabled={!reminderEnabled} className="date-field" />
              </div>
            </div>
          )}
        </section>

        {error && <Message severity="error" text={error} />}
        {success && <Message severity="success" text={success} />}

        <div className="invoice-create-footer">
          <div className="invoice-total">
            <span>Total</span>
            <strong>{displayTotal.toLocaleString('en-US', { style: 'currency', currency: hoursPreview?.currency || 'USD' })}</strong>
          </div>
          <Button
            type="submit"
            label={fromHours ? 'Generate from hours' : 'Create invoice'}
            icon={fromHours ? 'pi pi-clock' : 'pi pi-check'}
            className="submit-button"
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;
